/**
 * Triple-Dexel Stock Removal Engine
 * Professional CNC machining simulation - XY dexel grid with Z-depth spans
 * Performance: O(1) per tool sample, zero-allocation hot paths
 */

import * as THREE from 'three';

// ── Type Definitions ────────────────────────────────────────────────────────

export interface DexelBBox {
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;
}

export type ToolType = 'flat' | 'ball' | 'bull';

export interface DexelToolGeometry {
  type: ToolType;
  diameter: number;
  cornerRadius: number;
  length: number;
}

export interface ToolSample {
  x: number; y: number; z: number;
  i: number; j: number; k: number;
}

export interface DexelMeshLayout {
  topStart: number;
  bottomStart: number;
  frontStart: number;
  backStart: number;
  leftStart: number;
  rightStart: number;
  totalVertices: number;
}

// ── Constants ───────────────────────────────────────────────────────────────

const MAX_SPANS = 8;
const SPAN_SIZE = 2;

// ── Engine ──────────────────────────────────────────────────────────────────

export class TripleDexelEngine {

  // Grid config (readonly after construction)
  readonly resX: number;
  readonly resY: number;
  readonly resZ: number;
  readonly cellSize: number;
  readonly bbox: DexelBBox;

  // Dexel data (XY grid - Z depth spans)
  private xyData: Float32Array;
  private xySpanCounts: Uint8Array;

  // YZ grid (resY × resZ cells, X-depth spans)
  private yzData: Float32Array;
  private yzSpanCounts: Uint8Array;

  // XZ grid (resX × resZ cells, Y-depth spans)
  private xzData: Float32Array;
  private xzSpanCounts: Uint8Array;

  // Dirty tracking
  private dirtyMask: Uint8Array;
  private dirtyBounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  private dirtyCount = 0;

  // Tool ID tracking
  private toolIdMap: Uint16Array;
  private _currentToolId = 0;

  // Config
  silindirYonModu = 0; // 0=milling, 1=lathe

  // Stats
  private processedSamples = 0;

  // Pre-allocated buffers (zero GC pressure)
  private readonly _toolSample: ToolSample = { x: 0, y: 0, z: 0, i: 0, j: 0, k: 0 };
  private readonly _subResultMins = new Float32Array(MAX_SPANS * 2);
  private readonly _subResultMaxs = new Float32Array(MAX_SPANS * 2);
  private _heightMapBuf: Float32Array | null = null;
  private _bottomMapBuf: Float32Array | null = null;
  private _cutDepthBuf: Float32Array | null = null;
  private _smoothBufA: Float32Array | null = null;
  private _smoothBufB: Float32Array | null = null;
  private _aoBuf: Float32Array | null = null;
  private _dirtyIndicesBuf: Int32Array | null = null;
  private _dirtyHeightsBuf: Float32Array | null = null;
  private _dirtyBottomsBuf: Float32Array | null = null;
  private readonly _emptyIndices = new Int32Array(0);
  private readonly _emptyHeights = new Float32Array(0);
  private _edgeSegmentsBuf: Float32Array | null = null;

  // Mesh layout cache
  private _meshLayout: DexelMeshLayout | null = null;

  // Index buffer cache (topology is static — reuse across mesh rebuilds)
  private _indexCache: Uint32Array | null = null;
  private _indexCacheResX = 0;
  private _indexCacheResY = 0;

  // Material singleton (shared across all mesh instances)
  private static _sharedMaterial: THREE.MeshPhysicalMaterial | null = null;

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSTRUCTION
  // ═══════════════════════════════════════════════════════════════════════════

  constructor(bbox: DexelBBox, resolution: number = 1.0) {
    this.bbox = { ...bbox };
    this.cellSize = 1.0 / resolution;
    this.resX = Math.max(2, Math.ceil((bbox.maxX - bbox.minX) / this.cellSize));
    this.resY = Math.max(2, Math.ceil((bbox.maxY - bbox.minY) / this.cellSize));
    this.resZ = Math.max(2, Math.ceil((bbox.maxZ - bbox.minZ) / this.cellSize));

    const totalCellsXY = this.resX * this.resY;
    const totalCellsYZ = this.resY * this.resZ;
    const totalCellsXZ = this.resX * this.resZ;

    this.xyData = new Float32Array(totalCellsXY * MAX_SPANS * SPAN_SIZE);
    this.xySpanCounts = new Uint8Array(totalCellsXY);
    this.yzData = new Float32Array(totalCellsYZ * MAX_SPANS * SPAN_SIZE);
    this.yzSpanCounts = new Uint8Array(totalCellsYZ);
    this.xzData = new Float32Array(totalCellsXZ * MAX_SPANS * SPAN_SIZE);
    this.xzSpanCounts = new Uint8Array(totalCellsXZ);

    this.dirtyMask = new Uint8Array(totalCellsXY);
    this.toolIdMap = new Uint16Array(totalCellsXY);

    this.initializeStock();
  }

  private initializeStock(): void {
    const { minX, maxX, minY, maxY, minZ, maxZ } = this.bbox;

    const xyCells = this.resX * this.resY;
    for (let i = 0; i < xyCells; i++) {
      const off = i * MAX_SPANS * SPAN_SIZE;
      this.xyData[off] = minZ;
      this.xyData[off + 1] = maxZ;
      this.xySpanCounts[i] = 1;
    }

    const yzCells = this.resY * this.resZ;
    for (let i = 0; i < yzCells; i++) {
      const off = i * MAX_SPANS * SPAN_SIZE;
      this.yzData[off] = minX;
      this.yzData[off + 1] = maxX;
      this.yzSpanCounts[i] = 1;
    }

    const xzCells = this.resX * this.resZ;
    for (let i = 0; i < xzCells; i++) {
      const off = i * MAX_SPANS * SPAN_SIZE;
      this.xzData[off] = minY;
      this.xzData[off + 1] = maxY;
      this.xzSpanCounts[i] = 1;
    }
  }

  reset(): void {
    this.initializeStock();
    this.processedSamples = 0;
    this.clearDirty();
    this.toolIdMap.fill(0);
    this._currentToolId = 0;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DIRTY TRACKING
  // ═══════════════════════════════════════════════════════════════════════════

  private markDirty(ix: number, iy: number): void {
    const idx = iy * this.resX + ix;
    if (this.dirtyMask[idx] === 0) {
      this.dirtyMask[idx] = 1;
      this.dirtyCount++;
      if (ix < this.dirtyBounds.minX) this.dirtyBounds.minX = ix;
      if (ix > this.dirtyBounds.maxX) this.dirtyBounds.maxX = ix;
      if (iy < this.dirtyBounds.minY) this.dirtyBounds.minY = iy;
      if (iy > this.dirtyBounds.maxY) this.dirtyBounds.maxY = iy;
    }
  }

  clearDirty(): void {
    this.dirtyMask.fill(0);
    this.dirtyCount = 0;
    this.dirtyBounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  }

  getDirtyCount(): number { return this.dirtyCount; }

  getDirtyBounds(): { minX: number; maxX: number; minY: number; maxY: number } {
    return { ...this.dirtyBounds };
  }

  isDirty(ix: number, iy: number): boolean {
    return this.dirtyMask[iy * this.resX + ix] === 1;
  }

  getBBox(): DexelBBox { return { ...this.bbox }; }

  // ═══════════════════════════════════════════════════════════════════════════
  // TOOL GEOMETRY HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  private getToolRadiusAtHeight(tool: DexelToolGeometry, height: number): number {
    if (height < 0) return 0;
    const R = tool.diameter / 2;
    if (height > tool.length) return R;
    const r = tool.cornerRadius;
    switch (tool.type) {
      case 'flat': return R;
      case 'ball':
        if (height >= R) return R;
        return Math.sqrt(R * R - (R - height) * (R - height));
      case 'bull':
        if (height >= r) return R;
        const dy = r - height;
        return R - r + Math.sqrt(r * r - dy * dy);
      default: return R;
    }
  }

  private getToolHeightAtRadius(tool: DexelToolGeometry, radius: number): number {
    const R = tool.diameter / 2;
    if (radius > R) return -1;
    if (radius < 0) return 0;
    const r = tool.cornerRadius;
    switch (tool.type) {
      case 'flat': return 0;
      case 'ball': return R - Math.sqrt(R * R - radius * radius);
      case 'bull':
        if (radius <= R - r) return 0;
        const dx = radius - (R - r);
        return r - Math.sqrt(r * r - dx * dx);
      default: return 0;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CORE SPAN SUBTRACTION
  // ═══════════════════════════════════════════════════════════════════════════

  private subtractSpanFromColumn(
    data: Float32Array,
    spanCounts: Uint8Array,
    columnIdx: number,
    cutMin: number,
    cutMax: number
  ): void {
    const baseOffset = columnIdx * MAX_SPANS * SPAN_SIZE;
    const spanCount = spanCounts[columnIdx];
    if (spanCount === 0) return;

    // Safety: swap if inverted
    if (cutMin > cutMax) { const t = cutMin; cutMin = cutMax; cutMax = t; }
    if (cutMin === cutMax || !isFinite(cutMin) || !isFinite(cutMax)) return;

    const resultMins = this._subResultMins;
    const resultMaxs = this._subResultMaxs;
    let resultCount = 0;

    for (let i = 0; i < spanCount; i++) {
      const spanMin = data[baseOffset + i * SPAN_SIZE];
      const spanMax = data[baseOffset + i * SPAN_SIZE + 1];

      // Case 1: Cut completely outside span
      if (cutMax <= spanMin || cutMin >= spanMax) {
        resultMins[resultCount] = spanMin;
        resultMaxs[resultCount] = spanMax;
        resultCount++;
        continue;
      }
      // Case 2: Cut contains span entirely
      if (cutMin <= spanMin && cutMax >= spanMax) continue;
      // Case 3: Cut inside span → two spans
      if (cutMin > spanMin && cutMax < spanMax) {
        resultMins[resultCount] = spanMin;
        resultMaxs[resultCount] = cutMin;
        resultCount++;
        resultMins[resultCount] = cutMax;
        resultMaxs[resultCount] = spanMax;
        resultCount++;
        continue;
      }
      // Case 4: Cut overlaps left
      if (cutMin <= spanMin && cutMax < spanMax) {
        resultMins[resultCount] = cutMax;
        resultMaxs[resultCount] = spanMax;
        resultCount++;
        continue;
      }
      // Case 5: Cut overlaps right
      if (cutMin > spanMin && cutMax >= spanMax) {
        resultMins[resultCount] = spanMin;
        resultMaxs[resultCount] = cutMin;
        resultCount++;
        continue;
      }
    }

    // Write back
    const newCount = Math.min(resultCount, MAX_SPANS);
    for (let i = 0; i < newCount; i++) {
      data[baseOffset + i * SPAN_SIZE] = resultMins[i];
      data[baseOffset + i * SPAN_SIZE + 1] = resultMaxs[i];
    }
    spanCounts[columnIdx] = newCount;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TOOL PROCESSING
  // ═══════════════════════════════════════════════════════════════════════════

  processToolPosition(sample: ToolSample, tool: DexelToolGeometry): void {
    let { x, y, z, i: axisI, j: axisJ, k: axisK } = sample;

    if (isNaN(axisI) || isNaN(axisJ) || isNaN(axisK) ||
        (Math.abs(axisI) + Math.abs(axisJ) + Math.abs(axisK)) < 0.001) {
      axisI = 0; axisJ = 0; axisK = 1;
    }

    const absI = Math.abs(axisI);
    const absJ = Math.abs(axisJ);
    const absK = Math.abs(axisK);

    this._processOnXYGrid(x, y, z, axisI, axisJ, axisK, absK, tool);

    if (absI >= 0.3 && absI >= absJ && absI >= absK) {
      this._processOnYZGrid(x, y, z, axisI, tool);
    } else if (absJ >= 0.3 && absJ >= absI && absJ >= absK) {
      this._processOnXZGrid(x, y, z, axisJ, tool);
    }

    this.processedSamples++;
  }

  private _processOnXYGrid(
    x: number, y: number, z: number,
    axisI: number, axisJ: number, axisK: number, absK: number,
    tool: DexelToolGeometry
  ): void {
    const R = tool.diameter / 2;
    const R2 = R * R;
    const isBall = tool.type === 'ball';
    const isBull = tool.type === 'bull';
    const cornerR = tool.cornerRadius || 0;
    const flatR = R - cornerR;
    const flatR2 = flatR * flatR;
    const cornerR2 = cornerR * cornerR;
    const cellSize = this.cellSize;
    const cellSizeInv = 1 / cellSize;
    const currentToolId = this._currentToolId;
    const depthMin = this.bbox.minZ;
    const depthMax = this.bbox.maxZ;
    const planeMinX = this.bbox.minX;
    const planeMinY = this.bbox.minY;
    const resX = this.resX;
    const resY = this.resY;
    const toolLen = tool.length || tool.diameter * 3;

    const iuMin = Math.max(0, Math.floor((x - R - planeMinX) * cellSizeInv));
    const iuMax = Math.min(resX - 1, Math.ceil((x + R - planeMinX) * cellSizeInv));
    const ivMin = Math.max(0, Math.floor((y - R - planeMinY) * cellSizeInv));
    const ivMax = Math.min(resY - 1, Math.ceil((y + R - planeMinY) * cellSizeInv));

    for (let iv = ivMin; iv <= ivMax; iv++) {
      const rowOff = iv * resX;
      const worldY = planeMinY + (iv + 0.5) * cellSize;
      const dv = worldY - y;
      const dvSq = dv * dv;

      for (let iu = iuMin; iu <= iuMax; iu++) {
        const worldX = planeMinX + (iu + 0.5) * cellSize;
        const du = worldX - x;
        const dist2Sq = du * du + dvSq;
        if (dist2Sq > R2) continue;

        const colIdx = rowOff + iu;

        let tipZ = z;
        if (isBall) {
          tipZ = z + R - Math.sqrt(Math.max(0, R2 - dist2Sq));
        } else if (isBull && cornerR > 0 && dist2Sq > flatR2) {
          const dist2D = Math.sqrt(dist2Sq);
          if (dist2D < R) {
            const dfc = dist2D - flatR;
            if (dfc < cornerR) {
              tipZ = z + cornerR - Math.sqrt(cornerR2 - dfc * dfc);
            }
          }
        }

        let cutMin: number, cutMax: number;
        if (absK >= 0.3) {
          if (axisK >= 0) {
            if (tipZ >= depthMax) continue;
            cutMin = Math.max(tipZ, depthMin);
            cutMax = depthMax;
          } else {
            if (tipZ <= depthMin) continue;
            cutMin = depthMin;
            cutMax = Math.min(tipZ, depthMax);
          }
        } else {
          cutMin = Math.max(tipZ - toolLen * 0.5, depthMin);
          cutMax = Math.min(tipZ + toolLen * 0.5, depthMax);
        }

        if (cutMin >= cutMax) continue;

        this.subtractSpanFromColumn(this.xyData, this.xySpanCounts, colIdx, cutMin, cutMax);
        this.markDirty(iu, iv);
        if (currentToolId > 0) this.toolIdMap[colIdx] = currentToolId;
      }
    }
  }

  private _processOnYZGrid(
    x: number, y: number, z: number,
    axisI: number, tool: DexelToolGeometry
  ): void {
    const R = tool.diameter / 2;
    const R2 = R * R;
    const cellSize = this.cellSize;
    const cellSizeInv = 1 / cellSize;
    const depthMinX = this.bbox.minX;
    const depthMaxX = this.bbox.maxX;
    const planeMinY = this.bbox.minY;
    const planeMinZ = this.bbox.minZ;
    const resY = this.resY;
    const resZ = this.resZ;
    const toolLen = tool.length || tool.diameter * 3;
    const isBall = tool.type === 'ball';
    const isBull = tool.type === 'bull';
    const cornerR = tool.cornerRadius || 0;
    const flatR = R - cornerR;
    const flatR2 = flatR * flatR;
    const cornerR2 = cornerR * cornerR;
    const absI = Math.abs(axisI);

    const ivMin = Math.max(0, Math.floor((y - R - planeMinY) * cellSizeInv));
    const ivMax = Math.min(resY - 1, Math.ceil((y + R - planeMinY) * cellSizeInv));
    const iwMin = Math.max(0, Math.floor((z - R - planeMinZ) * cellSizeInv));
    const iwMax = Math.min(resZ - 1, Math.ceil((z + R - planeMinZ) * cellSizeInv));

    const dirtyIx = Math.max(0, Math.min(this.resX - 1, Math.floor((x - this.bbox.minX) * cellSizeInv)));

    for (let iw = iwMin; iw <= iwMax; iw++) {
      const worldZ = planeMinZ + (iw + 0.5) * cellSize;
      const dw = worldZ - z;
      const dwSq = dw * dw;

      for (let iv = ivMin; iv <= ivMax; iv++) {
        const worldY = planeMinY + (iv + 0.5) * cellSize;
        const dv = worldY - y;
        const dist2Sq = dv * dv + dwSq;
        if (dist2Sq > R2) continue;

        let tipX = x;
        if (isBall) {
          tipX = x + R - Math.sqrt(Math.max(0, R2 - dist2Sq));
        } else if (isBull && cornerR > 0 && dist2Sq > flatR2) {
          const dist2D = Math.sqrt(dist2Sq);
          if (dist2D < R) {
            const dfc = dist2D - flatR;
            if (dfc < cornerR) {
              tipX = x + cornerR - Math.sqrt(cornerR2 - dfc * dfc);
            }
          }
        }

        let cutMin: number, cutMax: number;
        if (absI >= 0.3) {
          if (axisI >= 0) {
            if (tipX >= depthMaxX) continue;
            cutMin = Math.max(tipX, depthMinX);
            cutMax = depthMaxX;
          } else {
            if (tipX <= depthMinX) continue;
            cutMin = depthMinX;
            cutMax = Math.min(tipX, depthMaxX);
          }
        } else {
          cutMin = Math.max(tipX - toolLen * 0.5, depthMinX);
          cutMax = Math.min(tipX + toolLen * 0.5, depthMaxX);
        }

        if (cutMin >= cutMax) continue;

        const colIdx = iw * resY + iv;
        this.subtractSpanFromColumn(this.yzData, this.yzSpanCounts, colIdx, cutMin, cutMax);
        this.markDirty(dirtyIx, iv);
      }
    }
  }

  private _processOnXZGrid(
    x: number, y: number, z: number,
    axisJ: number, tool: DexelToolGeometry
  ): void {
    const R = tool.diameter / 2;
    const R2 = R * R;
    const cellSize = this.cellSize;
    const cellSizeInv = 1 / cellSize;
    const depthMinY = this.bbox.minY;
    const depthMaxY = this.bbox.maxY;
    const planeMinX = this.bbox.minX;
    const planeMinZ = this.bbox.minZ;
    const resX = this.resX;
    const resZ = this.resZ;
    const toolLen = tool.length || tool.diameter * 3;
    const isBall = tool.type === 'ball';
    const isBull = tool.type === 'bull';
    const cornerR = tool.cornerRadius || 0;
    const flatR = R - cornerR;
    const flatR2 = flatR * flatR;
    const cornerR2 = cornerR * cornerR;
    const absJ = Math.abs(axisJ);

    const iuMin = Math.max(0, Math.floor((x - R - planeMinX) * cellSizeInv));
    const iuMax = Math.min(resX - 1, Math.ceil((x + R - planeMinX) * cellSizeInv));
    const iwMin = Math.max(0, Math.floor((z - R - planeMinZ) * cellSizeInv));
    const iwMax = Math.min(resZ - 1, Math.ceil((z + R - planeMinZ) * cellSizeInv));

    const dirtyIy = Math.max(0, Math.min(this.resY - 1, Math.floor((y - this.bbox.minY) * cellSizeInv)));

    for (let iw = iwMin; iw <= iwMax; iw++) {
      const worldZ = planeMinZ + (iw + 0.5) * cellSize;
      const dw = worldZ - z;
      const dwSq = dw * dw;

      for (let iu = iuMin; iu <= iuMax; iu++) {
        const worldX = planeMinX + (iu + 0.5) * cellSize;
        const du = worldX - x;
        const dist2Sq = du * du + dwSq;
        if (dist2Sq > R2) continue;

        let tipY = y;
        if (isBall) {
          tipY = y + R - Math.sqrt(Math.max(0, R2 - dist2Sq));
        } else if (isBull && cornerR > 0 && dist2Sq > flatR2) {
          const dist2D = Math.sqrt(dist2Sq);
          if (dist2D < R) {
            const dfc = dist2D - flatR;
            if (dfc < cornerR) {
              tipY = y + cornerR - Math.sqrt(cornerR2 - dfc * dfc);
            }
          }
        }

        let cutMin: number, cutMax: number;
        if (absJ >= 0.3) {
          if (axisJ >= 0) {
            if (tipY >= depthMaxY) continue;
            cutMin = Math.max(tipY, depthMinY);
            cutMax = depthMaxY;
          } else {
            if (tipY <= depthMinY) continue;
            cutMin = depthMinY;
            cutMax = Math.min(tipY, depthMaxY);
          }
        } else {
          cutMin = Math.max(tipY - toolLen * 0.5, depthMinY);
          cutMax = Math.min(tipY + toolLen * 0.5, depthMaxY);
        }

        if (cutMin >= cutMax) continue;

        const colIdx = iw * resX + iu;
        this.subtractSpanFromColumn(this.xzData, this.xzSpanCounts, colIdx, cutMin, cutMax);
        this.markDirty(iu, dirtyIy);
      }
    }
  }

  processToolPositionBatch(samples: ToolSample[], tool: DexelToolGeometry): number {
    let totalHits = 0;

    for (let si = 0; si < samples.length; si++) {
      const s = samples[si];
      let axisI = s.i, axisJ = s.j, axisK = s.k;
      if (isNaN(axisI) || isNaN(axisJ) || isNaN(axisK) ||
          (Math.abs(axisI) + Math.abs(axisJ) + Math.abs(axisK)) < 0.001) {
        axisI = 0; axisJ = 0; axisK = 1;
      }

      const absI = Math.abs(axisI);
      const absJ = Math.abs(axisJ);
      const absK = Math.abs(axisK);

      this._processOnXYGrid(s.x, s.y, s.z, axisI, axisJ, axisK, absK, tool);
      totalHits++;

      if (absI >= 0.3 && absI >= absJ && absI >= absK) {
        this._processOnYZGrid(s.x, s.y, s.z, axisI, tool);
      } else if (absJ >= 0.3 && absJ >= absI && absJ >= absK) {
        this._processOnXZGrid(s.x, s.y, s.z, axisJ, tool);
      }
    }

    this.processedSamples += samples.length;
    return totalHits;
  }

  processSweptMove(
    prevPos: THREE.Vector3,
    currPos: THREE.Vector3,
    toolAxis: THREE.Vector3,
    tool: DexelToolGeometry
  ): void {
    const dx = currPos.x - prevPos.x;
    const dy = currPos.y - prevPos.y;
    const dz = currPos.z - prevPos.z;
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

    const sample = this._toolSample;
    sample.i = toolAxis.x;
    sample.j = toolAxis.y;
    sample.k = toolAxis.z;

    if (length < 0.01) {
      sample.x = currPos.x;
      sample.y = currPos.y;
      sample.z = currPos.z;
      this.processToolPosition(sample, tool);
      return;
    }

    const toolR = tool.diameter / 2;
    const toolDia = tool.diameter;

    let stepSize: number;
    if (length < toolDia) {
      stepSize = Math.min(this.cellSize * 0.3, toolR * 0.2);
    } else if (length < toolDia * 5) {
      stepSize = Math.min(this.cellSize * 0.5, toolR * 0.4);
    } else {
      stepSize = Math.min(this.cellSize * 0.8, toolR * 0.6);
    }

    const numSteps = Math.max(1, Math.ceil(length / stepSize));
    const actualSteps = Math.min(numSteps, 500);
    const invSteps = 1.0 / actualSteps;

    for (let s = 0; s <= actualSteps; s++) {
      const t = s * invSteps;
      sample.x = prevPos.x + dx * t;
      sample.y = prevPos.y + dy * t;
      sample.z = prevPos.z + dz * t;
      this.processToolPosition(sample, tool);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DATA EXTRACTION
  // ═══════════════════════════════════════════════════════════════════════════

  extractHeightMap(): Float32Array {
    const totalCells = this.resX * this.resY;
    if (!this._heightMapBuf || this._heightMapBuf.length !== totalCells) {
      this._heightMapBuf = new Float32Array(totalCells);
    }
    const hm = this._heightMapBuf;
    const minZ = this.bbox.minZ;

    for (let i = 0; i < totalCells; i++) {
      const sc = this.xySpanCounts[i];
      const off = i * MAX_SPANS * SPAN_SIZE;
      if (sc === 0) { hm[i] = minZ; }
      else if (sc === 1) { hm[i] = this.xyData[off + 1]; }
      else {
        let max = -Infinity;
        for (let s = 0; s < sc; s++) {
          const v = this.xyData[off + s * SPAN_SIZE + 1];
          if (v > max) max = v;
        }
        hm[i] = max;
      }
    }
    return hm;
  }

  extractBottomMap(): Float32Array {
    const totalCells = this.resX * this.resY;
    if (!this._bottomMapBuf || this._bottomMapBuf.length !== totalCells) {
      this._bottomMapBuf = new Float32Array(totalCells);
    }
    const bm = this._bottomMapBuf;
    const minZ = this.bbox.minZ;

    for (let i = 0; i < totalCells; i++) {
      const sc = this.xySpanCounts[i];
      const off = i * MAX_SPANS * SPAN_SIZE;
      if (sc === 0) { bm[i] = minZ; }
      else if (sc === 1) { bm[i] = this.xyData[off]; }
      else {
        let min = Infinity;
        for (let s = 0; s < sc; s++) {
          const v = this.xyData[off + s * SPAN_SIZE];
          if (v < min) min = v;
        }
        bm[i] = min;
      }
    }
    return bm;
  }

  extractDirtyHeightUpdates(): { indices: Int32Array; heights: Float32Array; bottoms: Float32Array; count: number } {
    const count = this.dirtyCount;
    if (count === 0) return { indices: this._emptyIndices, heights: this._emptyHeights, bottoms: this._emptyHeights, count: 0 };

    if (!this._dirtyIndicesBuf || this._dirtyIndicesBuf.length < count) {
      this._dirtyIndicesBuf = new Int32Array(Math.max(count, 1024));
    }
    if (!this._dirtyHeightsBuf || this._dirtyHeightsBuf.length < count) {
      this._dirtyHeightsBuf = new Float32Array(Math.max(count, 1024));
    }
    if (!this._dirtyBottomsBuf || this._dirtyBottomsBuf.length < count) {
      this._dirtyBottomsBuf = new Float32Array(Math.max(count, 1024));
    }

    const indices = this._dirtyIndicesBuf;
    const heights = this._dirtyHeightsBuf;
    const bottoms = this._dirtyBottomsBuf;
    const minZ = this.bbox.minZ;
    let wi = 0;

    const { minX, maxX, minY, maxY } = this.dirtyBounds;
    for (let iy = minY; iy <= maxY && iy < this.resY; iy++) {
      for (let ix = minX; ix <= maxX && ix < this.resX; ix++) {
        const idx = iy * this.resX + ix;
        if (this.dirtyMask[idx] === 0) continue;

        const sc = this.xySpanCounts[idx];
        const off = idx * MAX_SPANS * SPAN_SIZE;

        if (sc === 0) {
          indices[wi] = idx; heights[wi] = minZ; bottoms[wi] = minZ; wi++;
        } else if (sc === 1) {
          indices[wi] = idx;
          heights[wi] = this.xyData[off + 1];
          bottoms[wi] = this.xyData[off];
          wi++;
        } else {
          let maxH = -Infinity, minH = Infinity;
          for (let s = 0; s < sc; s++) {
            const sMin = this.xyData[off + s * SPAN_SIZE];
            const sMax = this.xyData[off + s * SPAN_SIZE + 1];
            if (sMax > maxH) maxH = sMax;
            if (sMin < minH) minH = sMin;
          }
          indices[wi] = idx; heights[wi] = maxH; bottoms[wi] = minH; wi++;
        }
      }
    }
    return { indices, heights, bottoms, count: wi };
  }

  extractCutDepthMap(): Float32Array {
    const totalCells = this.resX * this.resY;
    if (!this._cutDepthBuf || this._cutDepthBuf.length !== totalCells) {
      this._cutDepthBuf = new Float32Array(totalCells);
    }
    const dm = this._cutDepthBuf;
    const totalDepth = this.bbox.maxZ - this.bbox.minZ;

    for (let i = 0; i < totalCells; i++) {
      const sc = this.xySpanCounts[i];
      if (sc === 0) { dm[i] = 1.0; continue; }
      const off = i * MAX_SPANS * SPAN_SIZE;
      let remaining = 0;
      for (let s = 0; s < sc; s++) {
        remaining += this.xyData[off + s * SPAN_SIZE + 1] - this.xyData[off + s * SPAN_SIZE];
      }
      dm[i] = 1.0 - remaining / totalDepth;
    }
    return dm;
  }

  setCurrentToolId(toolId: number): void { this._currentToolId = toolId; }
  extractToolIdMap(): Uint16Array { return this.toolIdMap; }
  getXYSpanCounts(): Uint8Array { return this.xySpanCounts; }

  // ═══════════════════════════════════════════════════════════════════════════
  // SMOOTHING (3x3 bilateral - edge preserving)
  // ═══════════════════════════════════════════════════════════════════════════

  // Precomputed 5x5 spatial Gaussian weights (σ=1.5, computed once)
  private static readonly _SMOOTH_SPATIAL_5x5 = (() => {
    const w = new Float32Array(25); // 5x5 = [-2..+2]
    const invSig2 = 1.0 / (2 * 1.5 * 1.5);
    for (let dy = -2; dy <= 2; dy++)
      for (let dx = -2; dx <= 2; dx++)
        w[(dy + 2) * 5 + (dx + 2)] = Math.exp(-(dx * dx + dy * dy) * invSig2);
    return w;
  })();

  smoothHeightMap(
    heightMap: Float32Array,
    iterations: number = 1,
    sigmaRange: number = 0,
    dirtyBounds?: { minX: number; maxX: number; minY: number; maxY: number }
  ): Float32Array {
    if (iterations <= 0) return heightMap;
    const resX = this.resX;
    const resY = this.resY;
    const totalCells = resX * resY;

    if (!this._smoothBufA || this._smoothBufA.length !== totalCells) this._smoothBufA = new Float32Array(totalCells);
    if (!this._smoothBufB || this._smoothBufB.length !== totalCells) this._smoothBufB = new Float32Array(totalCells);

    let src = heightMap;
    let dst = this._smoothBufA;
    const sigma = sigmaRange > 0 ? sigmaRange : this.cellSize * 0.6;
    const invSigSq = 1.0 / (sigma * sigma);
    const spanCounts = this.xySpanCounts;
    const sw = TripleDexelEngine._SMOOTH_SPATIAL_5x5;

    const loopMinY = dirtyBounds ? Math.max(0, dirtyBounds.minY - 2) : 0;
    const loopMaxY = dirtyBounds ? Math.min(resY - 1, dirtyBounds.maxY + 2) : resY - 1;
    const loopMinX = dirtyBounds ? Math.max(0, dirtyBounds.minX - 2) : 0;
    const loopMaxX = dirtyBounds ? Math.min(resX - 1, dirtyBounds.maxX + 2) : resX - 1;

    for (let iter = 0; iter < iterations; iter++) {
      if (dirtyBounds) {
        for (let iy = 0; iy < resY; iy++) {
          const rowOff = iy * resX;
          if (iy < loopMinY || iy > loopMaxY) {
            for (let ix = 0; ix < resX; ix++) dst[rowOff + ix] = src[rowOff + ix];
            continue;
          }
          for (let ix = 0; ix < resX; ix++) {
            if (ix < loopMinX || ix > loopMaxX) { dst[rowOff + ix] = src[rowOff + ix]; continue; }
            const idx = rowOff + ix;
            if (spanCounts[idx] === 0 || ix < 2 || ix >= resX - 2 || iy < 2 || iy >= resY - 2) {
              dst[idx] = src[idx]; continue;
            }
            const cH = src[idx];
            let sum = 0, weight = 0;
            for (let dy = -2; dy <= 2; dy++) {
              const ri = (iy + dy) * resX;
              const swRow = (dy + 2) * 5;
              for (let dx = -2; dx <= 2; dx++) {
                const nIdx = ri + ix + dx;
                if (spanCounts[nIdx] === 0) continue;
                const diff = src[nIdx] - cH;
                const rangeW = 1.0 / (1.0 + diff * diff * invSigSq);
                const w = sw[swRow + dx + 2] * rangeW;
                sum += src[nIdx] * w;
                weight += w;
              }
            }
            dst[idx] = weight > 0 ? sum / weight : cH;
          }
        }
      } else {
        for (let iy = 0; iy < resY; iy++) {
          for (let ix = 0; ix < resX; ix++) {
            const idx = iy * resX + ix;
            if (spanCounts[idx] === 0 || ix < 2 || ix >= resX - 2 || iy < 2 || iy >= resY - 2) {
              dst[idx] = src[idx]; continue;
            }
            const cH = src[idx];
            let sum = 0, weight = 0;
            for (let dy = -2; dy <= 2; dy++) {
              const ri = (iy + dy) * resX;
              const swRow = (dy + 2) * 5;
              for (let dx = -2; dx <= 2; dx++) {
                const nIdx = ri + ix + dx;
                if (spanCounts[nIdx] === 0) continue;
                const diff = src[nIdx] - cH;
                const rangeW = 1.0 / (1.0 + diff * diff * invSigSq);
                const w = sw[swRow + dx + 2] * rangeW;
                sum += src[nIdx] * w;
                weight += w;
              }
            }
            dst[idx] = weight > 0 ? sum / weight : cH;
          }
        }
      }

      if (iter < iterations - 1) {
        src = dst;
        dst = (dst === this._smoothBufA) ? this._smoothBufB! : this._smoothBufA!;
      }
    }
    return dst;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYTICAL NORMALS (heightmap gradient - smooth shading for slopes)
  // ═══════════════════════════════════════════════════════════════════════════

  private _normalBuf: Float32Array | null = null;

  /**
   * Heightmap gradient'inden per-vertex analitik normaller hesapla.
   * Central differences kullanır - eğimli yüzeyler smooth, düz yüzeyler flat.
   * flatShading:false ile kullanılır → GPU normalleri interpolate eder → pürüzsüz eğimler.
   */
  computeHeightMapNormals(heightMap: Float32Array): Float32Array {
    const resX = this.resX;
    const resY = this.resY;
    const totalCells = resX * resY;
    const cs = this.cellSize;
    const inv2cs = 1.0 / (2.0 * cs);

    if (!this._normalBuf || this._normalBuf.length !== totalCells * 3) {
      this._normalBuf = new Float32Array(totalCells * 3);
    }
    const normals = this._normalBuf;

    // Inner cells: duvar-farkındalıklı gradyan
    // Merkezi fark dik duvarı (delik/cep kenarı) komşuya taşıyıp ağız çevresinde
    // eğik normal halkası oluşturuyordu → delik üstten konik/havşalı görünüyordu.
    // Duvar eşiğini aşan komşu farkı gradyana katılmaz (tek taraflı fark kullanılır).
    const invCs = 1.0 / cs;
    const duvarEsik = cs * 4;   // ~76°'den dik geçişler duvar sayılır
    for (let iy = 1; iy < resY - 1; iy++) {
      const rowOff = iy * resX;
      for (let ix = 1; ix < resX - 1; ix++) {
        const ci = rowOff + ix;
        const h = heightMap[ci];
        const dxL = h - heightMap[ci - 1], dxR = heightMap[ci + 1] - h;
        const dyD = h - heightMap[ci - resX], dyU = heightMap[ci + resX] - h;
        const axL = Math.abs(dxL), axR = Math.abs(dxR);
        const ayD = Math.abs(dyD), ayU = Math.abs(dyU);
        let gx: number, gy: number;
        if (axL > duvarEsik && axR > duvarEsik) gx = 0;
        else if (axL > duvarEsik) gx = dxR * invCs;
        else if (axR > duvarEsik) gx = dxL * invCs;
        else gx = (dxL + dxR) * 0.5 * invCs; // merkezi fark
        if (ayD > duvarEsik && ayU > duvarEsik) gy = 0;
        else if (ayD > duvarEsik) gy = dyU * invCs;
        else if (ayU > duvarEsik) gy = dyD * invCs;
        else gy = (dyD + dyU) * 0.5 * invCs;
        const nx = -gx;
        const ny = -gy;
        // Fast path: flat surfaces (majority of uncut stock) → normal = (0,0,1)
        const gradSq = nx * nx + ny * ny;
        if (gradSq < 1e-8) {
          normals[ci * 3] = 0;
          normals[ci * 3 + 1] = 0;
          normals[ci * 3 + 2] = 1;
        } else {
          const invLen = 1.0 / Math.sqrt(gradSq + 1);
          normals[ci * 3] = nx * invLen;
          normals[ci * 3 + 1] = ny * invLen;
          normals[ci * 3 + 2] = invLen;
        }
      }
    }

    // Boundary cells: up normal (simplification - boundaries are stock edges)
    for (let ix = 0; ix < resX; ix++) {
      // Top row (iy=0)
      normals[ix * 3] = 0; normals[ix * 3 + 1] = 0; normals[ix * 3 + 2] = 1;
      // Bottom row (iy=resY-1)
      const ci = (resY - 1) * resX + ix;
      normals[ci * 3] = 0; normals[ci * 3 + 1] = 0; normals[ci * 3 + 2] = 1;
    }
    for (let iy = 1; iy < resY - 1; iy++) {
      // Left col (ix=0)
      const ci0 = iy * resX;
      normals[ci0 * 3] = 0; normals[ci0 * 3 + 1] = 0; normals[ci0 * 3 + 2] = 1;
      // Right col (ix=resX-1)
      const ciR = ci0 + resX - 1;
      normals[ciR * 3] = 0; normals[ciR * 3 + 1] = 0; normals[ciR * 3 + 2] = 1;
    }
    return normals;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AMBIENT OCCLUSION (horizon-based, tangent approximation)
  // ═══════════════════════════════════════════════════════════════════════════

  computeVertexAO(
    heightMap: Float32Array,
    maxRadius: number = 5,
    dirtyBounds?: { minX: number; maxX: number; minY: number; maxY: number }
  ): Float32Array {
    const resX = this.resX;
    const resY = this.resY;
    const totalCells = resX * resY;
    if (!this._aoBuf || this._aoBuf.length !== totalCells) this._aoBuf = new Float32Array(totalCells);
    const ao = this._aoBuf;
    const cellSize = this.cellSize;
    const spanCounts = this.xySpanCounts;
    const stockTopZ = this.bbox.maxZ;
    const stockMinZ = this.bbox.minZ;
    const stockDepth = stockTopZ - stockMinZ;

    const DIRS = 8;
    const cos45 = 0.7071;
    const dxArr = [1, cos45, 0, -cos45, -1, -cos45,  0,  cos45];
    const dyArr = [0, cos45, 1,  cos45,  0, -cos45, -1, -cos45];
    const invDirs = 1.0 / DIRS;
    const marchSteps = Math.max(3, maxRadius);

    const uncutThresh = stockTopZ - 0.01;

    const loopMinY = dirtyBounds ? Math.max(0, dirtyBounds.minY - maxRadius) : 0;
    const loopMaxY = dirtyBounds ? Math.min(resY - 1, dirtyBounds.maxY + maxRadius) : resY - 1;
    const loopMinX = dirtyBounds ? Math.max(0, dirtyBounds.minX - maxRadius) : 0;
    const loopMaxX = dirtyBounds ? Math.min(resX - 1, dirtyBounds.maxX + maxRadius) : resX - 1;

    for (let iy = loopMinY; iy <= loopMaxY; iy++) {
      const rowOff = iy * resX;
      for (let ix = loopMinX; ix <= loopMaxX; ix++) {
        const idx = rowOff + ix;
        if (spanCounts[idx] === 0) { ao[idx] = 0; continue; }

        const cH = heightMap[idx];
        if (cH >= uncutThresh) { ao[idx] = 0; continue; }

        let totalOcc = 0;

        for (let d = 0; d < DIRS; d++) {
          const ddx = dxArr[d], ddy = dyArr[d];
          let maxTan = 0;

          for (let step = 1; step <= marchSteps; step++) {
            const sx = (ix + ddx * step + 0.5) | 0;
            const sy = (iy + ddy * step + 0.5) | 0;
            if (sx < 0 || sx >= resX || sy < 0 || sy >= resY) break;

            const nIdx = sy * resX + sx;
            if (spanCounts[nIdx] === 0) continue;
            const tanA = (heightMap[nIdx] - cH) / (step * cellSize);
            if (tanA > maxTan) maxTan = tanA;
          }

          if (maxTan > 0) {
            totalOcc += maxTan / Math.sqrt(1 + maxTan * maxTan);
          }
        }

        const cutDepth = stockTopZ - cH;
        const depthAO = stockDepth > 0 ? cutDepth / stockDepth * 0.10 : 0;

        ao[idx] = Math.min(1, Math.max(0, totalOcc * invDirs + depthAO));
      }
    }
    return ao;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MESH LAYOUT & GENERATION
  // ═══════════════════════════════════════════════════════════════════════════

  getMeshLayout(): DexelMeshLayout {
    if (!this._meshLayout) {
      const topVerts = this.resX * this.resY;
      this._meshLayout = {
        topStart: 0,
        bottomStart: topVerts,
        frontStart: topVerts * 2,
        backStart: topVerts * 2 + this.resX * 2,
        leftStart: topVerts * 2 + this.resX * 4,
        rightStart: topVerts * 2 + this.resX * 4 + this.resY * 2,
        totalVertices: topVerts * 2 + (this.resX + this.resY) * 4
      };
    }
    return this._meshLayout;
  }

  createProfessionalStockMesh(
    color: number = 0xB8C0C8,
    aoIntensity: number = 0.6,
    aoRadius: number = 4,
    smoothPasses: number = 0
  ): THREE.Mesh {
    let topMap = this.extractHeightMap();
    if (smoothPasses > 0) topMap = this.smoothHeightMap(topMap, smoothPasses);
    const bottomMap = this.extractBottomMap();
    const aoMap = this.computeVertexAO(topMap, aoRadius);
    const normalMap = this.computeHeightMapNormals(topMap);
    const layout = this.getMeshLayout();

    const resX = this.resX;
    const resY = this.resY;
    const cellSize = this.cellSize;
    const bbox = this.bbox;
    const stockTopZ = bbox.maxZ;

    const positions = new Float32Array(layout.totalVertices * 3);
    const normals = new Float32Array(layout.totalVertices * 3);
    const colors = new Float32Array(layout.totalVertices * 3);

    const baseR = ((color >> 16) & 0xff) / 255;
    const baseG = ((color >> 8) & 0xff) / 255;
    const baseB = (color & 0xff) / 255;

    let vi = 0;

    // ─── Top Surface (smooth shading with analytical heightmap normals) ───
    const stockDepth = stockTopZ - bbox.minZ;
    const invStockDepth = stockDepth > 0 ? 1.0 / stockDepth : 0;
    for (let iy = 0; iy < resY; iy++) {
      for (let ix = 0; ix < resX; ix++) {
        const ci = iy * resX + ix;
        positions[vi * 3] = bbox.minX + ix * cellSize;
        positions[vi * 3 + 1] = bbox.minY + iy * cellSize;
        positions[vi * 3 + 2] = topMap[ci];
        // Analytical normals from heightmap gradient → smooth shading on slopes
        normals[vi * 3] = normalMap[ci * 3];
        normals[vi * 3 + 1] = normalMap[ci * 3 + 1];
        normals[vi * 3 + 2] = normalMap[ci * 3 + 2];
        // AO only on cut surfaces (prevents grid pattern on uncut flat surfaces)
        const isUncut = Math.abs(topMap[ci] - stockTopZ) < 0.01;
        const aoF = isUncut ? 1.0 : (1.0 - aoMap[ci] * aoIntensity);
        // Depth gradient: progressive darkening for cavity depth perception
        const depthT = isUncut ? 0 : (stockTopZ - topMap[ci]) * invStockDepth;
        const depthDarken = 1.0 - depthT * 0.08;
        colors[vi * 3] = baseR * aoF * depthDarken;
        colors[vi * 3 + 1] = baseG * aoF * depthDarken;
        colors[vi * 3 + 2] = baseB * aoF * depthDarken;
        vi++;
      }
    }

    // ─── Bottom Surface ───
    for (let iy = 0; iy < resY; iy++) {
      for (let ix = 0; ix < resX; ix++) {
        const ci = iy * resX + ix;
        positions[vi * 3] = bbox.minX + ix * cellSize;
        positions[vi * 3 + 1] = bbox.minY + iy * cellSize;
        positions[vi * 3 + 2] = bottomMap[ci];
        normals[vi * 3 + 2] = -1;
        const aoF = (1.0 - aoMap[ci] * aoIntensity) * 0.92;
        colors[vi * 3] = baseR * aoF;
        colors[vi * 3 + 1] = baseG * aoF;
        colors[vi * 3 + 2] = baseB * aoF;
        vi++;
      }
    }

    // ─── Side Walls (4 boundary edges) ───
    const emitSide = (x: number, y: number, ci: number, nx: number, ny: number, nz: number) => {
      const aoBase = (1.0 - aoMap[ci] * aoIntensity);
      const topF = aoBase * 0.90;
      positions[vi * 3] = x; positions[vi * 3 + 1] = y; positions[vi * 3 + 2] = topMap[ci];
      normals[vi * 3] = nx; normals[vi * 3 + 1] = ny; normals[vi * 3 + 2] = nz;
      colors[vi * 3] = baseR * topF; colors[vi * 3 + 1] = baseG * topF; colors[vi * 3 + 2] = baseB * topF;
      vi++;
      const botF = aoBase * 0.72;
      positions[vi * 3] = x; positions[vi * 3 + 1] = y; positions[vi * 3 + 2] = bottomMap[ci];
      normals[vi * 3] = nx; normals[vi * 3 + 1] = ny; normals[vi * 3 + 2] = nz;
      colors[vi * 3] = baseR * botF; colors[vi * 3 + 1] = baseG * botF; colors[vi * 3 + 2] = baseB * botF;
      vi++;
    };

    // Front (Y = minY)
    for (let ix = 0; ix < resX; ix++) emitSide(bbox.minX + ix * cellSize, bbox.minY, ix, 0, -1, 0);
    // Back (Y = maxY)
    for (let ix = 0; ix < resX; ix++) emitSide(bbox.minX + ix * cellSize, bbox.maxY, (resY - 1) * resX + ix, 0, 1, 0);
    // Left (X = minX)
    for (let iy = 0; iy < resY; iy++) emitSide(bbox.minX, bbox.minY + iy * cellSize, iy * resX, -1, 0, 0);
    // Right (X = maxX)
    for (let iy = 0; iy < resY; iy++) emitSide(bbox.maxX, bbox.minY + iy * cellSize, iy * resX + (resX - 1), 1, 0, 0);

    // ─── Indices (static grid topology — cached across rebuilds) ───
    let indices: Uint32Array;
    if (this._indexCache && this._indexCacheResX === resX && this._indexCacheResY === resY) {
      indices = this._indexCache;
    } else {
      const topQuads = (resX - 1) * (resY - 1);
      const sideQuads = (resX - 1) * 2 + (resY - 1) * 2;
      const totalQuads = topQuads * 2 + sideQuads;
      indices = new Uint32Array(totalQuads * 6);
      let ii = 0;

      for (let iy = 0; iy < resY - 1; iy++) {
        for (let ix = 0; ix < resX - 1; ix++) {
          const tl = layout.topStart + iy * resX + ix;
          const tr = tl + 1, bl = tl + resX, br = bl + 1;
          indices[ii++] = tl; indices[ii++] = bl; indices[ii++] = tr;
          indices[ii++] = tr; indices[ii++] = bl; indices[ii++] = br;
        }
      }

      for (let iy = 0; iy < resY - 1; iy++) {
        for (let ix = 0; ix < resX - 1; ix++) {
          const tl = layout.bottomStart + iy * resX + ix;
          const tr = tl + 1, bl = tl + resX, br = bl + 1;
          indices[ii++] = tl; indices[ii++] = tr; indices[ii++] = bl;
          indices[ii++] = tr; indices[ii++] = br; indices[ii++] = bl;
        }
      }

      for (let ix = 0; ix < resX - 1; ix++) {
        const tl = layout.frontStart + ix * 2, tr = tl + 2, bl = tl + 1, br = tr + 1;
        indices[ii++] = tl; indices[ii++] = tr; indices[ii++] = bl;
        indices[ii++] = tr; indices[ii++] = br; indices[ii++] = bl;
      }

      for (let ix = 0; ix < resX - 1; ix++) {
        const tl = layout.backStart + ix * 2, tr = tl + 2, bl = tl + 1, br = tr + 1;
        indices[ii++] = tl; indices[ii++] = bl; indices[ii++] = tr;
        indices[ii++] = tr; indices[ii++] = bl; indices[ii++] = br;
      }

      for (let iy = 0; iy < resY - 1; iy++) {
        const tl = layout.leftStart + iy * 2, tr = tl + 2, bl = tl + 1, br = tr + 1;
        indices[ii++] = tl; indices[ii++] = bl; indices[ii++] = tr;
        indices[ii++] = tr; indices[ii++] = bl; indices[ii++] = br;
      }

      for (let iy = 0; iy < resY - 1; iy++) {
        const tl = layout.rightStart + iy * 2, tr = tl + 2, bl = tl + 1, br = tr + 1;
        indices[ii++] = tl; indices[ii++] = tr; indices[ii++] = bl;
        indices[ii++] = tr; indices[ii++] = br; indices[ii++] = bl;
      }

      this._indexCache = indices;
      this._indexCacheResX = resX;
      this._indexCacheResY = resY;
    }

    // ─── Build Geometry ───
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    if (!TripleDexelEngine._sharedMaterial) {
      TripleDexelEngine._sharedMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        roughness: 0.38,
        metalness: 0.92,
        clearcoat: 0.12,
        clearcoatRoughness: 0.3,
        envMapIntensity: 0.95,
        side: THREE.DoubleSide,
        vertexColors: true,
        flatShading: false,
      });
    }
    const material = TripleDexelEngine._sharedMaterial;

    return new THREE.Mesh(geometry, material);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INTERNAL WALL QUADS (pocket/cliff vertical faces)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Generate internal wall geometry for steep height transitions.
   * Returns a separate non-indexed triangle mesh (6 verts per wall quad).
   * Used to fill the vertical gaps between adjacent cells with large height differences.
   */
  createInternalWallMesh(
    heightMap: Float32Array,
    bottomMap: Float32Array,
    color: { r: number; g: number; b: number },
    threshold: number,
    cellColors?: Float32Array // per-cell RGB (ci*3+0/1/2), e.g. main mesh top surface colors
  ): THREE.Mesh | null {
    const resX = this.resX;
    const resY = this.resY;
    const cellSize = this.cellSize;
    const bbox = this.bbox;
    const spanCounts = this.xySpanCounts;

    // First pass: count steep transitions
    let transitionCount = 0;
    for (let iy = 0; iy < resY; iy++) {
      const rowOff = iy * resX;
      for (let ix = 0; ix < resX; ix++) {
        const ci = rowOff + ix;
        if (spanCounts[ci] === 0) continue;
        const h = heightMap[ci];
        if (ix < resX - 1 && spanCounts[ci + 1] > 0) {
          if (Math.abs(h - heightMap[ci + 1]) > threshold) transitionCount++;
        }
        if (iy < resY - 1 && spanCounts[ci + resX] > 0) {
          if (Math.abs(h - heightMap[ci + resX]) > threshold) transitionCount++;
        }
      }
    }
    if (transitionCount === 0) return null;

    // Each transition: 1 wall quad (6v) + 1 horizontal cap quad (6v) = 12 vertices
    const vertCount = transitionCount * 12;
    const positions = new Float32Array(vertCount * 3);
    const normals = new Float32Array(vertCount * 3);
    const colors = new Float32Array(vertCount * 3);
    let vi = 0;

    const hasCellColors = cellColors && cellColors.length >= resX * resY * 3;

    // Second pass: generate wall + cap quads
    for (let iy = 0; iy < resY; iy++) {
      const rowOff = iy * resX;
      for (let ix = 0; ix < resX; ix++) {
        const ci = rowOff + ix;
        if (spanCounts[ci] === 0) continue;
        const h = heightMap[ci];

        // Right neighbor (X+1)
        if (ix < resX - 1 && spanCounts[ci + 1] > 0) {
          const hn = heightMap[ci + 1];
          const diff = h - hn;
          if (Math.abs(diff) > threshold) {
            const xBound = bbox.minX + (ix + 1) * cellSize;
            const yLo = bbox.minY + iy * cellSize;
            const yHi = bbox.minY + (iy + 1) * cellSize;
            const zHi = Math.max(h, hn);
            const zLo = Math.min(h, hn);
            const nx = diff > 0 ? 1 : -1;

            // Determine HIGH cell index for color lookup
            const highCi = h >= hn ? ci : ci + 1;
            let wR: number, wG: number, wB: number, cR: number, cG: number, cB: number;
            if (hasCellColors) {
              cR = cellColors![highCi * 3]; cG = cellColors![highCi * 3 + 1]; cB = cellColors![highCi * 3 + 2];
            } else {
              cR = color.r; cG = color.g; cB = color.b;
            }
            wR = cR * 0.82; wG = cG * 0.82; wB = cB * 0.82;
            const wR2 = wR * 0.9, wG2 = wG * 0.9, wB2 = wB * 0.9;

            // ─── Vertical wall quad (6 vertices) ───
            positions[vi * 3] = xBound; positions[vi * 3 + 1] = yLo; positions[vi * 3 + 2] = zHi;
            normals[vi * 3] = nx; colors[vi * 3] = wR; colors[vi * 3 + 1] = wG; colors[vi * 3 + 2] = wB; vi++;
            positions[vi * 3] = xBound; positions[vi * 3 + 1] = yLo; positions[vi * 3 + 2] = zLo;
            normals[vi * 3] = nx; colors[vi * 3] = wR2; colors[vi * 3 + 1] = wG2; colors[vi * 3 + 2] = wB2; vi++;
            positions[vi * 3] = xBound; positions[vi * 3 + 1] = yHi; positions[vi * 3 + 2] = zHi;
            normals[vi * 3] = nx; colors[vi * 3] = wR; colors[vi * 3 + 1] = wG; colors[vi * 3 + 2] = wB; vi++;
            positions[vi * 3] = xBound; positions[vi * 3 + 1] = yHi; positions[vi * 3 + 2] = zHi;
            normals[vi * 3] = nx; colors[vi * 3] = wR; colors[vi * 3 + 1] = wG; colors[vi * 3 + 2] = wB; vi++;
            positions[vi * 3] = xBound; positions[vi * 3 + 1] = yLo; positions[vi * 3 + 2] = zLo;
            normals[vi * 3] = nx; colors[vi * 3] = wR2; colors[vi * 3 + 1] = wG2; colors[vi * 3 + 2] = wB2; vi++;
            positions[vi * 3] = xBound; positions[vi * 3 + 1] = yHi; positions[vi * 3 + 2] = zLo;
            normals[vi * 3] = nx; colors[vi * 3] = wR2; colors[vi * 3 + 1] = wG2; colors[vi * 3 + 2] = wB2; vi++;

            // ─── Horizontal cap at zHi covering the slope quad (6 vertices) ───
            const xCapLo = bbox.minX + ix * cellSize;
            // Tri1: (xLo,yLo,z), (xHi,yLo,z), (xLo,yHi,z) — CCW from above
            positions[vi * 3] = xCapLo; positions[vi * 3 + 1] = yLo; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            positions[vi * 3] = xBound; positions[vi * 3 + 1] = yLo; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            positions[vi * 3] = xCapLo; positions[vi * 3 + 1] = yHi; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            // Tri2: (xHi,yLo,z), (xHi,yHi,z), (xLo,yHi,z) — CCW from above
            positions[vi * 3] = xBound; positions[vi * 3 + 1] = yLo; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            positions[vi * 3] = xBound; positions[vi * 3 + 1] = yHi; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            positions[vi * 3] = xCapLo; positions[vi * 3 + 1] = yHi; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
          }
        }

        // Top neighbor (Y+1)
        if (iy < resY - 1 && spanCounts[ci + resX] > 0) {
          const hn = heightMap[ci + resX];
          const diff = h - hn;
          if (Math.abs(diff) > threshold) {
            const yBound = bbox.minY + (iy + 1) * cellSize;
            const xLo = bbox.minX + ix * cellSize;
            const xHi = bbox.minX + (ix + 1) * cellSize;
            const zHi = Math.max(h, hn);
            const zLo = Math.min(h, hn);
            const ny = diff > 0 ? 1 : -1;

            // Determine HIGH cell index for color lookup
            const highCi = h >= hn ? ci : ci + resX;
            let wR: number, wG: number, wB: number, cR: number, cG: number, cB: number;
            if (hasCellColors) {
              cR = cellColors![highCi * 3]; cG = cellColors![highCi * 3 + 1]; cB = cellColors![highCi * 3 + 2];
            } else {
              cR = color.r; cG = color.g; cB = color.b;
            }
            wR = cR * 0.82; wG = cG * 0.82; wB = cB * 0.82;
            const wR2 = wR * 0.9, wG2 = wG * 0.9, wB2 = wB * 0.9;

            // ─── Vertical wall quad (6 vertices) ───
            positions[vi * 3] = xLo; positions[vi * 3 + 1] = yBound; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 1] = ny; colors[vi * 3] = wR; colors[vi * 3 + 1] = wG; colors[vi * 3 + 2] = wB; vi++;
            positions[vi * 3] = xLo; positions[vi * 3 + 1] = yBound; positions[vi * 3 + 2] = zLo;
            normals[vi * 3 + 1] = ny; colors[vi * 3] = wR2; colors[vi * 3 + 1] = wG2; colors[vi * 3 + 2] = wB2; vi++;
            positions[vi * 3] = xHi; positions[vi * 3 + 1] = yBound; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 1] = ny; colors[vi * 3] = wR; colors[vi * 3 + 1] = wG; colors[vi * 3 + 2] = wB; vi++;
            positions[vi * 3] = xHi; positions[vi * 3 + 1] = yBound; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 1] = ny; colors[vi * 3] = wR; colors[vi * 3 + 1] = wG; colors[vi * 3 + 2] = wB; vi++;
            positions[vi * 3] = xLo; positions[vi * 3 + 1] = yBound; positions[vi * 3 + 2] = zLo;
            normals[vi * 3 + 1] = ny; colors[vi * 3] = wR2; colors[vi * 3 + 1] = wG2; colors[vi * 3 + 2] = wB2; vi++;
            positions[vi * 3] = xHi; positions[vi * 3 + 1] = yBound; positions[vi * 3 + 2] = zLo;
            normals[vi * 3 + 1] = ny; colors[vi * 3] = wR2; colors[vi * 3 + 1] = wG2; colors[vi * 3 + 2] = wB2; vi++;

            // ─── Horizontal cap at zHi covering the slope quad (6 vertices) ───
            const yCapLo = bbox.minY + iy * cellSize;
            // Tri1: (xLo,yCapLo,z), (xHi,yCapLo,z), (xLo,yBound,z) — CCW from above
            positions[vi * 3] = xLo; positions[vi * 3 + 1] = yCapLo; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            positions[vi * 3] = xHi; positions[vi * 3 + 1] = yCapLo; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            positions[vi * 3] = xLo; positions[vi * 3 + 1] = yBound; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            // Tri2: (xHi,yCapLo,z), (xHi,yBound,z), (xLo,yBound,z) — CCW from above
            positions[vi * 3] = xHi; positions[vi * 3 + 1] = yCapLo; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            positions[vi * 3] = xHi; positions[vi * 3 + 1] = yBound; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
            positions[vi * 3] = xLo; positions[vi * 3 + 1] = yBound; positions[vi * 3 + 2] = zHi;
            normals[vi * 3 + 2] = 1; colors[vi * 3] = cR; colors[vi * 3 + 1] = cG; colors[vi * 3 + 2] = cB; vi++;
          }
        }
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      roughness: 0.45,
      metalness: 0.88,
      envMapIntensity: 0.7,
      side: THREE.FrontSide, // caps only visible from above, walls from pocket side
      vertexColors: true,
      flatShading: true,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4,
    });

    return new THREE.Mesh(geometry, material);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EDGE DETECTION (for edge line rendering)
  // ═══════════════════════════════════════════════════════════════════════════

  extractEdgeSegments(threshold: number): Float32Array {
    const heightMap = this.extractHeightMap();
    const resX = this.resX;
    const resY = this.resY;
    const cellSize = this.cellSize;
    const bbox = this.bbox;

    // Estimate max edges (worst case: every cell boundary is an edge)
    const maxEdges = (resX - 1) * resY + resX * (resY - 1);
    const maxFloats = maxEdges * 12; // 2 line points × 3 coords × 2 segments (vert + horiz)
    if (!this._edgeSegmentsBuf || this._edgeSegmentsBuf.length < maxFloats) {
      this._edgeSegmentsBuf = new Float32Array(maxFloats);
    }
    const buf = this._edgeSegmentsBuf;
    let fi = 0;

    // Horizontal edges (X direction)
    for (let iy = 0; iy < resY; iy++) {
      for (let ix = 0; ix < resX - 1; ix++) {
        const idxA = iy * resX + ix;
        const idxB = idxA + 1;
        const hA = heightMap[idxA], hB = heightMap[idxB];
        if (Math.abs(hA - hB) > threshold) {
          const x = bbox.minX + (ix + 1) * cellSize;
          const y = bbox.minY + iy * cellSize;
          const lo = Math.min(hA, hB), hi = Math.max(hA, hB);
          // Vertical line
          buf[fi++] = x; buf[fi++] = y; buf[fi++] = lo;
          buf[fi++] = x; buf[fi++] = y; buf[fi++] = hi;
        }
      }
    }

    // Vertical edges (Y direction)
    for (let iy = 0; iy < resY - 1; iy++) {
      for (let ix = 0; ix < resX; ix++) {
        const idxA = iy * resX + ix;
        const idxB = idxA + resX;
        const hA = heightMap[idxA], hB = heightMap[idxB];
        if (Math.abs(hA - hB) > threshold) {
          const x = bbox.minX + ix * cellSize;
          const y = bbox.minY + (iy + 1) * cellSize;
          const lo = Math.min(hA, hB), hi = Math.max(hA, hB);
          buf[fi++] = x; buf[fi++] = y; buf[fi++] = lo;
          buf[fi++] = x; buf[fi++] = y; buf[fi++] = hi;
        }
      }
    }

    return buf.subarray(0, fi);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SNAPSHOT SYSTEM (undo/redo)
  // ═══════════════════════════════════════════════════════════════════════════

  getSnapshot(): {
    xyData: Float32Array; xySpanCounts: Uint8Array;
    yzData: Float32Array; yzSpanCounts: Uint8Array;
    xzData: Float32Array; xzSpanCounts: Uint8Array;
  } {
    return {
      xyData: new Float32Array(this.xyData),
      xySpanCounts: new Uint8Array(this.xySpanCounts),
      yzData: new Float32Array(this.yzData),
      yzSpanCounts: new Uint8Array(this.yzSpanCounts),
      xzData: new Float32Array(this.xzData),
      xzSpanCounts: new Uint8Array(this.xzSpanCounts),
    };
  }

  restoreSnapshot(snapshot: {
    xyData: Float32Array; xySpanCounts: Uint8Array;
    yzData?: Float32Array; yzSpanCounts?: Uint8Array;
    xzData?: Float32Array; xzSpanCounts?: Uint8Array;
  }): void {
    this.xyData.set(snapshot.xyData);
    this.xySpanCounts.set(snapshot.xySpanCounts);
    if (snapshot.yzData && snapshot.yzSpanCounts) {
      this.yzData.set(snapshot.yzData);
      this.yzSpanCounts.set(snapshot.yzSpanCounts);
    }
    if (snapshot.xzData && snapshot.xzSpanCounts) {
      this.xzData.set(snapshot.xzData);
      this.xzSpanCounts.set(snapshot.xzSpanCounts);
    }
    this.dirtyMask.fill(1);
    this.dirtyCount = this.resX * this.resY;
    this.dirtyBounds = { minX: 0, maxX: this.resX - 1, minY: 0, maxY: this.resY - 1 };
  }

  resetToInitialState(): void {
    const { minX, maxX, minY, maxY, minZ, maxZ } = this.bbox;

    const xyCells = this.resX * this.resY;
    for (let i = 0; i < xyCells; i++) {
      const off = i * MAX_SPANS * SPAN_SIZE;
      this.xyData[off] = minZ;
      this.xyData[off + 1] = maxZ;
      this.xySpanCounts[i] = 1;
    }

    const yzCells = this.resY * this.resZ;
    for (let i = 0; i < yzCells; i++) {
      const off = i * MAX_SPANS * SPAN_SIZE;
      this.yzData[off] = minX;
      this.yzData[off + 1] = maxX;
      this.yzSpanCounts[i] = 1;
    }

    const xzCells = this.resX * this.resZ;
    for (let i = 0; i < xzCells; i++) {
      const off = i * MAX_SPANS * SPAN_SIZE;
      this.xzData[off] = minY;
      this.xzData[off + 1] = maxY;
      this.xzSpanCounts[i] = 1;
    }

    this.dirtyMask.fill(1);
    this.dirtyCount = xyCells;
    this.dirtyBounds = { minX: 0, maxX: this.resX - 1, minY: 0, maxY: this.resY - 1 };
    this.processedSamples = 0;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  isPointInMaterial(x: number, y: number, z: number): boolean {
    const ix = Math.floor((x - this.bbox.minX) / this.cellSize);
    const iy = Math.floor((y - this.bbox.minY) / this.cellSize);
    const iz = Math.floor((z - this.bbox.minZ) / this.cellSize);

    if (ix < 0 || ix >= this.resX || iy < 0 || iy >= this.resY) return false;
    const idxXY = iy * this.resX + ix;
    const scXY = this.xySpanCounts[idxXY];
    const offXY = idxXY * MAX_SPANS * SPAN_SIZE;
    let hasZ = false;
    for (let i = 0; i < scXY; i++) {
      if (z >= this.xyData[offXY + i * SPAN_SIZE] && z <= this.xyData[offXY + i * SPAN_SIZE + 1]) {
        hasZ = true; break;
      }
    }
    if (!hasZ) return false;

    if (iy >= 0 && iy < this.resY && iz >= 0 && iz < this.resZ) {
      const idxYZ = iz * this.resY + iy;
      const scYZ = this.yzSpanCounts[idxYZ];
      if (scYZ > 0) {
        const offYZ = idxYZ * MAX_SPANS * SPAN_SIZE;
        let hasX = false;
        for (let i = 0; i < scYZ; i++) {
          if (x >= this.yzData[offYZ + i * SPAN_SIZE] && x <= this.yzData[offYZ + i * SPAN_SIZE + 1]) {
            hasX = true; break;
          }
        }
        if (!hasX) return false;
      }
    }

    if (ix >= 0 && ix < this.resX && iz >= 0 && iz < this.resZ) {
      const idxXZ = iz * this.resX + ix;
      const scXZ = this.xzSpanCounts[idxXZ];
      if (scXZ > 0) {
        const offXZ = idxXZ * MAX_SPANS * SPAN_SIZE;
        let hasY = false;
        for (let i = 0; i < scXZ; i++) {
          if (y >= this.xzData[offXZ + i * SPAN_SIZE] && y <= this.xzData[offXZ + i * SPAN_SIZE + 1]) {
            hasY = true; break;
          }
        }
        if (!hasY) return false;
      }
    }

    return true;
  }

  getResolution(): { resX: number; resY: number; resZ: number; cellSize: number } {
    return { resX: this.resX, resY: this.resY, resZ: this.resZ, cellSize: this.cellSize };
  }

  getStats(): { samples: number; memoryMB: number } {
    const mem = this.xyData.byteLength + this.xySpanCounts.byteLength
      + this.yzData.byteLength + this.yzSpanCounts.byteLength
      + this.xzData.byteLength + this.xzSpanCounts.byteLength;
    return { samples: this.processedSamples, memoryMB: mem / 1024 / 1024 };
  }

  dispose(): void {
    this.xyData = new Float32Array(0);
    this.xySpanCounts = new Uint8Array(0);
    this.yzData = new Float32Array(0);
    this.yzSpanCounts = new Uint8Array(0);
    this.xzData = new Float32Array(0);
    this.xzSpanCounts = new Uint8Array(0);
    this.toolIdMap = new Uint16Array(0);
    this.dirtyMask = new Uint8Array(0);
    this._heightMapBuf = null;
    this._bottomMapBuf = null;
    this._cutDepthBuf = null;
    this._smoothBufA = null;
    this._smoothBufB = null;
    this._aoBuf = null;
    this._dirtyIndicesBuf = null;
    this._dirtyHeightsBuf = null;
    this._dirtyBottomsBuf = null;
    this._edgeSegmentsBuf = null;
    this._meshLayout = null;
    this._indexCache = null;
    this._indexCacheResX = 0;
    this._indexCacheResY = 0;
  }
}
